import { useCallback, useEffect, useMemo, useState } from "react";
import { createSupplier, deleteSupplier, getSuppliers, updateSupplier } from "../../../api";
import { logError } from "../../../logging/loggingService";
import { ADDRESS_REQUIRED_MESSAGES } from "../../../utils/addressData";
import { getEmailError, getPhoneError } from "../../../utils/contactValidation";
import { emptySupplier, getSupplierGroupKey, normalizeInput } from "./supplierUtils";

export const useSupplierManagement = ({ hasAccounting }) => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentSupplier, setCurrentSupplier] = useState(emptySupplier());
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const [deleteAsk, setDeleteAsk] = useState(null);

    const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

    const fetchSuppliers = useCallback(() => {
        setLoading(true);
        setError(null);
        return getSuppliers()
            .then((res) => setSuppliers(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                logError("Fehler beim Laden der Lieferanten", err, { component: "SupplierManagement" });
                setError("Lieferanten konnten nicht geladen werden.");
                setSuppliers([]);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!hasAccounting) {
            setSuppliers([]);
            setLoading(false);
            setError(null);
            return;
        }
        fetchSuppliers();
    }, [hasAccounting, fetchSuppliers]);

    const filteredSuppliers = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = suppliers;
        if (q) {
            list = list.filter((supplier) => {
                const fields = [
                    supplier?.name || "",
                    supplier?.street || "",
                    supplier?.zip || "",
                    supplier?.city || "",
                    supplier?.country || "",
                    supplier?.email || "",
                    supplier?.phone || "",
                ];
                return fields.some((field) => field.toLowerCase().includes(q));
            });
        }
        return [...list].sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "de"));
    }, [query, suppliers]);

    const groupedSuppliers = useMemo(() => {
        const groups = new Map();
        filteredSuppliers.forEach((supplier) => {
            const group = getSupplierGroupKey(supplier);
            if (!groups.has(group)) {
                groups.set(group, []);
            }
            groups.get(group).push(supplier);
        });

        const keys = Array.from(groups.keys()).sort((a, b) => {
            if (a === "#") return 1;
            if (b === "#") return -1;
            return a.localeCompare(b, "de");
        });

        return keys.map((key) => ({
            key,
            suppliers: groups.get(key),
        }));
    }, [filteredSuppliers]);

    const handleFieldChange = (field, value) => {
        setCurrentSupplier((prev) => ({ ...prev, [field]: value }));
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleDialogOpen = () => {
        setCurrentSupplier(emptySupplier());
        setFieldErrors({});
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setCurrentSupplier(emptySupplier());
        setSaving(false);
        setFieldErrors({});
    };

    const handleEdit = (supplier) => {
        setCurrentSupplier({ ...supplier, country: supplier.country || emptySupplier().country });
        setFieldErrors({});
        setDialogOpen(true);
    };

    const handleSave = async () => {
        const payload = {
            id: currentSupplier.id || null,
            version: currentSupplier.version ?? null,
            name: normalizeInput(currentSupplier.name),
            street: normalizeInput(currentSupplier.street),
            zip: normalizeInput(currentSupplier.zip),
            city: normalizeInput(currentSupplier.city),
            country: normalizeInput(currentSupplier.country),
            email: normalizeInput(currentSupplier.email),
            phone: normalizeInput(currentSupplier.phone),
            notes: normalizeInput(currentSupplier.notes),
        };

        if (!payload.name) {
            setFieldErrors({ name: "Bitte einen Lieferantennamen angeben." });
            setToast({ open: true, msg: "Bitte prüfe die markierten Felder.", severity: "error" });
            return;
        }
        const nextErrors = {};
        if (!payload.street) nextErrors.street = ADDRESS_REQUIRED_MESSAGES.street;
        if (!payload.zip) nextErrors.zip = ADDRESS_REQUIRED_MESSAGES.zip;
        if (!payload.city) nextErrors.city = ADDRESS_REQUIRED_MESSAGES.city;
        if (!payload.country) nextErrors.country = ADDRESS_REQUIRED_MESSAGES.country;
        const emailError = getEmailError(payload.email);
        if (emailError) nextErrors.email = emailError;
        const phoneError = getPhoneError(payload.phone);
        if (phoneError) nextErrors.phone = phoneError;
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            setToast({ open: true, msg: "Bitte prüfe die markierten Felder.", severity: "error" });
            return;
        }

        try {
            setSaving(true);
            if (currentSupplier.id) {
                const res = await updateSupplier(currentSupplier.id, payload);
                setSuppliers((prev) => prev.map((s) => (s.id === currentSupplier.id ? res.data : s)));
                setToast({ open: true, msg: "Lieferant aktualisiert.", severity: "success" });
            } else {
                const res = await createSupplier(payload);
                setSuppliers((prev) => [...prev, res.data]);
                setToast({ open: true, msg: "Lieferant angelegt.", severity: "success" });
            }
            handleDialogClose();
        } catch (err) {
            logError("Fehler beim Speichern des Lieferanten", err, { component: "SupplierManagement" });
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const backendFieldErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const field = rawField.split(".").pop();
                    backendFieldErrors[field] = message;
                });
                if (Object.keys(backendFieldErrors).length > 0) {
                    setFieldErrors(backendFieldErrors);
                    setToast({
                        open: true,
                        msg: backendError?.message || "Bitte prüfe die markierten Felder.",
                        severity: "error",
                    });
                    return;
                }
            }
            setToast({ open: true, msg: "Speichern fehlgeschlagen.", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteAsk) return;
        const supplier = deleteAsk;
        setDeleteAsk(null);
        setSuppliers((prev) => prev.filter((s) => s.id !== supplier.id));
        try {
            await deleteSupplier(supplier.id);
            setToast({ open: true, msg: "Lieferant gelöscht.", severity: "success" });
        } catch (err) {
            logError("Fehler beim Löschen des Lieferanten", err, { component: "SupplierManagement" });
            setToast({ open: true, msg: "Löschen fehlgeschlagen.", severity: "error" });
            fetchSuppliers();
        }
    };

    const handleRefresh = () => {
        fetchSuppliers();
        setToast({ open: true, msg: "Aktualisiert.", severity: "success" });
    };

    const closeToast = () => setToast((prev) => ({ ...prev, open: false }));

    return {
        suppliers,
        loading,
        error,
        query,
        filteredSuppliers,
        groupedSuppliers,
        dialogOpen,
        currentSupplier,
        saving,
        fieldErrors,
        deleteAsk,
        toast,
        setError,
        setQuery,
        setDeleteAsk,
        handleFieldChange,
        handleDialogOpen,
        handleDialogClose,
        handleEdit,
        handleSave,
        confirmDelete,
        handleRefresh,
        closeToast,
    };
};
